import { useCallback, useEffect, useRef, useState } from 'react'
import { useWhisperTranscription } from './useWhisperTranscription'

// Extend Window interface for webkit speech recognition
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  isFinal: boolean
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition
}

interface SpeechRecognitionErrorEvent extends Event {
  error: 'no-speech' | 'aborted' | 'audio-capture' | 'network' | 'not-allowed' | 'service-not-allowed' | 'bad-grammar' | 'language-not-supported'
  message?: string
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  start(): void
  stop(): void
  abort(): void
  onstart: ((event: Event) => void) | null
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onend: ((event: Event) => void) | null
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }
}

interface UseSpeechRecognitionOptions {
  onEnd?: (finalTranscript: string, hadInterimAtStop: string) => void
  onError?: (error: string, errorType: string) => void
}

export function useSpeechRecognition(options?: UseSpeechRecognitionOptions) {
  const [useFallback, setUseFallback] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [interimTranscript, setInterimTranscript] = useState('')
  const [isSupported, setIsSupported] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const onEndRef = useRef(options?.onEnd)
  const onErrorRef = useRef(options?.onError)
  const fullTranscriptRef = useRef('')
  const interimAtStopRef = useRef('')
  const isStartingRef = useRef(false)
  const startTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Initialize Whisper fallback
  const whisperFallback = useWhisperTranscription(options)

  // Keep the callback refs up to date
  useEffect(() => {
    onEndRef.current = options?.onEnd
    onErrorRef.current = options?.onError
  }, [options?.onEnd, options?.onError])

  // Function to create/recreate the recognition object
  const createRecognition = useCallback(() => {
    const SpeechRecognitionAPI =
      (typeof window !== 'undefined' &&
        (window.SpeechRecognition || window.webkitSpeechRecognition)) ||
      null

    if (!SpeechRecognitionAPI) {
      return null
    }

    const recognition = new SpeechRecognitionAPI()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'

    // Only set isListening to true when recognition actually starts (after permission granted)
    recognition.onstart = () => {
      // Clear the timeout since we successfully started
      if (startTimeoutRef.current) {
        clearTimeout(startTimeoutRef.current)
        startTimeoutRef.current = null
      }
      isStartingRef.current = false
      setIsListening(true)
    }

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = ''
      let final = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const transcriptText = result[0].transcript

        if (result.isFinal) {
          final += `${transcriptText} `
        } else {
          interim += transcriptText
        }
      }

      if (final) {
        fullTranscriptRef.current += final
        setTranscript((prev) => prev + final)
      }
      setInterimTranscript(interim)
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // Clear the timeout since we got a response (even if error)
      if (startTimeoutRef.current) {
        clearTimeout(startTimeoutRef.current)
        startTimeoutRef.current = null
      }
      isStartingRef.current = false

      // If we get a network error and fallback is supported, switch immediately and retry
      if (event.error === 'network' && whisperFallback.isSupported) {
        setUseFallback(true)
        setIsListening(false)
        setError(null)

        // Retry with fallback on next tick
        setTimeout(() => {
          whisperFallback.startListening()
        }, 0)
        return
      }

      let errorMessage = ''

      switch (event.error) {
        case 'network':
          errorMessage = 'Speech recognition unavailable in this browser.'
          break
        case 'not-allowed':
        case 'service-not-allowed':
          errorMessage = 'Microphone access denied. Please enable microphone permissions in your browser settings.'
          break
        case 'no-speech':
          errorMessage = 'No speech detected. Please try again.'
          break
        case 'audio-capture':
          errorMessage = 'Microphone not found or not working. Please check your audio settings.'
          break
        case 'aborted':
          // This is normal when user stops listening, don't show error
          break
        default:
          errorMessage = `Speech recognition error: ${event.error}`
      }

      if (errorMessage) {
        setError(errorMessage)
        if (onErrorRef.current) {
          onErrorRef.current(errorMessage, event.error)
        }
      }

      setIsListening(false)
    }

    recognition.onend = () => {
      isStartingRef.current = false
      setIsListening(false)
      const capturedInterim = interimAtStopRef.current
      setInterimTranscript('')
      interimAtStopRef.current = ''

      // Wait a moment to ensure all final results have been processed
      setTimeout(() => {
        // Call the onEnd callback if provided with the final transcript and any interim that was present at stop
        if (onEndRef.current) {
          onEndRef.current(fullTranscriptRef.current, capturedInterim)
        }
      }, 200)
    }

    return recognition
  }, [])

  useEffect(() => {
    // Check if speech recognition is supported
    const SpeechRecognitionAPI =
      (typeof window !== 'undefined' &&
        (window.SpeechRecognition || window.webkitSpeechRecognition)) ||
      null

    if (SpeechRecognitionAPI) {
      setIsSupported(true)
      recognitionRef.current = createRecognition()
    } else {
      setIsSupported(false)
    }

    return () => {
      if (startTimeoutRef.current) {
        clearTimeout(startTimeoutRef.current)
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort()
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    }
  }, [createRecognition])

  const startListening = useCallback(() => {
    // If using fallback, delegate to Whisper
    if (useFallback) {
      whisperFallback.startListening()
      return
    }

    if (isListening || isStartingRef.current) {
      return
    }

    setTranscript('')
    setInterimTranscript('')
    setError(null) // Clear any previous errors
    fullTranscriptRef.current = ''
    isStartingRef.current = true

    // If recognition object doesn't exist or is in a bad state, recreate it
    if (!recognitionRef.current) {
      recognitionRef.current = createRecognition()
    }

    if (!recognitionRef.current) {
      isStartingRef.current = false
      const errorMsg = 'Speech recognition is not supported in this browser.'
      setError(errorMsg)
      if (onErrorRef.current) {
        onErrorRef.current(errorMsg, 'not-supported')
      }
      return
    }

    try {
      recognitionRef.current.start()
      // Note: isListening will be set to true in the onstart handler
      // This ensures we only show "listening" state after permission is granted
      
      // Set a timeout in case permission dialog is dismissed or hangs
      startTimeoutRef.current = setTimeout(() => {
        if (isStartingRef.current) {
          isStartingRef.current = false
          setIsListening(false)
          const errorMsg = 'Microphone request timed out. Please try again.'
          setError(errorMsg)
          if (onErrorRef.current) {
            onErrorRef.current(errorMsg, 'timeout')
          }
          // Try to abort any pending recognition
          try {
            recognitionRef.current?.abort()
          } catch (e) {
            // Ignore
          }
        }
      }, 5000) // 5 second timeout
    } catch (error) {
      isStartingRef.current = false

      // If we get an error starting, try recreating the recognition object
      try {
        recognitionRef.current.abort()
      } catch (e) {
        // Ignore abort errors
      }

      recognitionRef.current = createRecognition()

      const errorMsg = 'Failed to start speech recognition. The microphone may already be in use. Please try again.'
      setError(errorMsg)
      if (onErrorRef.current) {
        onErrorRef.current(errorMsg, 'start-failed')
      }
    }
  }, [isListening, createRecognition, useFallback, whisperFallback])

  const stopListening = useCallback(
    (currentInterim: string) => {
      // If using fallback, delegate to Whisper
      if (useFallback) {
        whisperFallback.stopListening()
        return
      }

      if (recognitionRef.current && isListening) {
        // Capture the current interim transcript so we can include it if it doesn't get finalized
        interimAtStopRef.current = currentInterim
        try {
          recognitionRef.current.stop()
        } catch (error) {
          // Force reset state if stop fails
          isStartingRef.current = false
          setIsListening(false)
        }
      }
    },
    [isListening, useFallback, whisperFallback]
  )

  const resetTranscript = useCallback(() => {
    setTranscript('')
    setInterimTranscript('')
    if (useFallback && whisperFallback.resetTranscript) {
      whisperFallback.resetTranscript()
    }
  }, [useFallback, whisperFallback.resetTranscript])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Return combined interface that works with both native and fallback
  return {
    isListening: useFallback ? whisperFallback.isListening : isListening,
    transcript: useFallback ? whisperFallback.transcript : transcript,
    interimTranscript: useFallback ? whisperFallback.interimTranscript : interimTranscript,
    isSupported: isSupported || whisperFallback.isSupported,
    error: useFallback ? whisperFallback.error : error,
    startListening,
    stopListening,
    resetTranscript,
    clearError,
  }
}
